/**
 * The mechanics bench, as a scene target: pulley machines and connected bodies.
 *
 * See docs/FeatureDesigns/question-scenes/question-scenes-target.md for the design.
 *
 * The setup is what a textbook says — "a block and tackle of 4 segments, 50 kgf
 * load, lower block 2 kg" — and the arrangement is built by the bench's own
 * preset builders, which already know how a rope has to be threaded for its
 * runs to be vertical. Writing the engine's world directly would ask a model
 * for port ids and rope lengths, and it would get the geometry wrong long
 * before it got the physics wrong.
 */
import * as M from '../mechanics';
import { BenchManifest, BenchSolution, Reading, ReadoutDecl } from './types';

type Load = { mass: number } | { weight: number };

interface MachineCommon {
    load: Load;
    /** kg, of each movable pulley — for a block and tackle, the whole lower block. */
    pulleyMass?: number;
    /**
     * N — the effort the question says was ACTUALLY needed, when it is more
     * than the machine needs ideally. The difference is friction, which the
     * bench does not model: its pulleys stay frictionless and give the VR, and
     * MA and efficiency are taken from the stated load and this effort. The
     * alternative the compiler reached for was inventing a lower-block weight
     * to soak up the difference — right numbers on a bench that shows a heavy
     * block the question never mentioned.
     */
    measuredEffort?: number;
    /** The same, stated in kgf — converted with the g the bench is solving
     *  under, so an unstated g tried as 10 converts it as 10 too. */
    measuredEffortKgf?: number;
    /** m the load is raised, for distances and work. */
    loadRise?: number;
    /** Absent when the question does not state it: 9.8, and the check tries 10. */
    g?: number;
}

export type MechanicsSetup =
    | ({ arrangement: 'single-fixed' | 'single-movable' | 'movable-tied-off' } & MachineCommon)
    | ({ arrangement: 'block-and-tackle'; segments: number } & MachineCommon)
    | ({ arrangement: 'movable-combination'; movable: number } & MachineCommon)
    | { arrangement: 'atwood'; mass1: number; mass2: number; g?: number }
    | { arrangement: 'table-pulley'; onTable: number; hanging: number; mu: number; g?: number }
    | { arrangement: 'incline'; mass: number; angle: number; mu: number; g?: number };

/** g when the question leaves it unstated. The check also tries 10 — see the manifest. */
const DEFAULT_G = 9.8;
const gOf = (s: { g?: number }): number => s.g ?? DEFAULT_G;

const MACHINES = ['single-fixed', 'single-movable', 'movable-tied-off', 'block-and-tackle', 'movable-combination'] as const;
const BODIES = ['atwood', 'table-pulley', 'incline'] as const;
type Machine = typeof MACHINES[number];

const isMachine = (s: MechanicsSetup): s is Extract<MechanicsSetup, { load: Load }> =>
    (MACHINES as readonly string[]).includes(s.arrangement);

/** Which sheaves move with the load, per arrangement — the ones a pulley's weight goes on. */
function movableSheaves(s: Extract<MechanicsSetup, { load: Load }>): string[] {
    switch (s.arrangement) {
        case 'single-movable': return ['p1'];
        case 'movable-tied-off': return ['pMov'];
        case 'block-and-tackle': return ['d0'];
        case 'movable-combination': return Array.from({ length: s.movable }, (_, k) => `m${k}`);
        default: return [];
    }
}

const loadMass = (load: Load, g: number): number => ('mass' in load ? load.mass : load.weight / g);

const setMass = (w: M.World, id: string, mass: number): M.World => ({
    ...w,
    parts: w.parts.map(p => (p.id === id ? { ...p, data: { ...p.data, mass } } : p)),
});

export function buildMechanics(s: MechanicsSetup): M.World {
    let w: M.World;
    switch (s.arrangement) {
        case 'single-fixed': w = M.fixedPulley(); break;
        case 'single-movable': w = M.movablePulley(); break;
        case 'movable-tied-off': w = M.movableTied(); break;
        case 'block-and-tackle': w = M.tackle(s.segments); break;
        case 'movable-combination': w = M.movableSystem(s.movable); break;
        case 'atwood': w = M.atwood(s.mass1, s.mass2); break;
        case 'table-pulley': w = M.onTable(s.mu, s.onTable, s.hanging); break;
        case 'incline': w = M.onRamp(s.mu, (s.angle * Math.PI) / 180, s.mass); break;
    }
    if (isMachine(s)) {
        w = setMass(w, 'load', loadMass(s.load, gOf(s)));
        for (const id of movableSheaves(s)) w = setMass(w, id, s.pulleyMass ?? 0);
    }
    return { ...w, gravity: gOf(s) };
}

// ── Validation ──────────────────────────────────────────────────────────────

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function positive(errors: string[], v: unknown, name: string, max: number, min = 0): void {
    if (!num(v)) errors.push(`${name} must be a number`);
    else if (v <= min || v > max) errors.push(`${name} must be more than ${min} and at most ${max} (got ${v})`);
}

function validateMechanics(raw: unknown): string[] {
    const errors: string[] = [];
    if (!raw || typeof raw !== 'object') return ['setup must be an object'];
    const s = raw as Record<string, unknown>;
    const arrangement = s.arrangement as string;
    if (![...MACHINES, ...BODIES].includes(arrangement as Machine)) {
        return [`arrangement must be one of ${[...MACHINES, ...BODIES].join(', ')} (got ${JSON.stringify(arrangement)})`];
    }
    if (s.g !== undefined && (!num(s.g) || (s.g !== 9.8 && s.g !== 9.81 && s.g !== 10))) {
        errors.push('g must be 9.8, 9.81 or 10 as the question states it — or left out when the question does not state it');
    }

    if ((MACHINES as readonly string[]).includes(arrangement)) {
        const load = s.load as Record<string, unknown> | undefined;
        if (!load || typeof load !== 'object') errors.push('load must be { mass } in kg (a load in kgf is its mass in kg) or { weight } in N');
        else if ('mass' in load) positive(errors, load.mass, 'load.mass', 100000);
        else if ('weight' in load) positive(errors, load.weight, 'load.weight', 1e6);
        else errors.push('load must have mass (kg) or weight (N)');
        if (s.pulleyMass !== undefined) {
            if (!num(s.pulleyMass) || s.pulleyMass < 0 || s.pulleyMass > 10000) errors.push('pulleyMass must be a number of kg, 0 or more');
            if (arrangement === 'single-fixed' && num(s.pulleyMass) && s.pulleyMass > 0) {
                errors.push('a fixed pulley does not rise with the load, so its mass changes nothing — leave pulleyMass out');
            }
        }
        if (s.loadRise !== undefined) positive(errors, s.loadRise, 'loadRise', 1000);
        if (s.measuredEffort !== undefined) positive(errors, s.measuredEffort, 'measuredEffort (N)', 1e6);
        if (s.measuredEffortKgf !== undefined) positive(errors, s.measuredEffortKgf, 'measuredEffortKgf', 1e5);
        if (s.measuredEffort !== undefined && s.measuredEffortKgf !== undefined) errors.push('give measuredEffort (N) or measuredEffortKgf, not both');
        if (arrangement === 'block-and-tackle' && !(Number.isInteger(s.segments) && (s.segments as number) >= 2 && (s.segments as number) <= 6)) {
            errors.push('segments must be a whole number from 2 to 6 — the number of rope segments holding the lower block, which is the VR');
        }
        if (arrangement === 'movable-combination' && !(Number.isInteger(s.movable) && (s.movable as number) >= 1 && (s.movable as number) <= 4)) {
            errors.push('movable must be a whole number from 1 to 4 — the number of movable pulleys, each on its own string');
        }
        if ((s.measuredEffort !== undefined || s.measuredEffortKgf !== undefined) && s.pulleyMass) {
            errors.push('give pulleyMass OR measuredEffort, not both: a stated effort already includes whatever the lower block costs');
        }
    } else if (s.measuredEffort !== undefined || s.measuredEffortKgf !== undefined) {
        errors.push('measuredEffort is only for pulley machines');
    } else if (arrangement === 'atwood') {
        positive(errors, s.mass1, 'mass1', 10000);
        positive(errors, s.mass2, 'mass2', 10000);
    } else if (arrangement === 'table-pulley') {
        positive(errors, s.onTable, 'onTable', 10000);
        positive(errors, s.hanging, 'hanging', 10000);
        if (!num(s.mu) || s.mu < 0 || s.mu > 2) errors.push('mu must be from 0 (smooth) to 2');
    } else if (arrangement === 'incline') {
        positive(errors, s.mass, 'mass', 10000);
        if (!num(s.angle) || s.angle < 5 || s.angle > 60) errors.push('angle must be in degrees, from 5 to 60');
        if (!num(s.mu) || s.mu < 0 || s.mu > 2) errors.push('mu must be from 0 (smooth) to 2');
    }
    return errors;
}

// ── Solving ─────────────────────────────────────────────────────────────────

interface Solved {
    w: M.World;
    bodies: M.Body[];
    sol: M.Solution;
    fbs: M.FreeBody[];
}

function solveWorld(w: M.World): Solved {
    const bodies = M.bodiesOf(w);
    const cons = M.constraintsOf(w, bodies);
    const sol = M.solve({ world: w, bodies, constraints: cons });
    return { w, bodies, sol, fbs: M.freeBodies(w, bodies, cons, sol) };
}

const stopping = (w: M.World, s: M.Solution): string[] => [
    ...(s.singular ? ['the arrangement does not settle to one answer'] : []),
    ...M.diagnose(w).filter(p => p.level === 'stops' && !p.whileStill).map(p => p.text),
];

/** Of a body, how fast it is speeding up, whichever way it is free to go. */
function accelerationOf(x: Solved, partId: string): number {
    const body = x.bodies.find(b => b.partIds.includes(partId));
    if (!body) return 0;
    const a = x.sol.acceleration[body.id] ?? [];
    const v = body.axes.reduce((s, ax, i) => ({ x: s.x + ax.x * (a[i] ?? 0), y: s.y + ax.y * (a[i] ?? 0) }), { x: 0, y: 0 });
    return Math.hypot(v.x, v.y);
}

/** What the anchors in the ceiling carry between them. */
function ceilingLoad(x: Solved): number {
    const anchorBodies = new Set(x.bodies.filter(b => b.partIds.some(id => M.partById(x.w, id)?.kind === 'anchor')).map(b => b.id));
    return x.fbs.filter(f => anchorBodies.has(f.bodyId)).reduce((sum, f) => sum + M.carried(f), 0);
}

const tension = (x: Solved, ropeId: string): Reading => x.sol.force[M.spanId(ropeId, 0)] ?? null;

function solveMachine(s: Extract<MechanicsSetup, { load: Load }>): BenchSolution {
    const w = buildMechanics(s);
    const x = solveWorld(w);
    const problems = stopping(w, x.sol);
    const d = M.derive(w, x.sol, x.fbs);
    /* The load a textbook means is the thing being lifted, not the pulleys it
     * hangs from. The bench's own `derive` counts the lower block in the load —
     * the honest answer to "what is the rope holding up" — and on that
     * definition a heavy lower block costs no efficiency at all. The book puts
     * the block's weight on the loss side, so this does. */
    const g = gOf(s);
    const load = loadMass(s.load, g) * g;
    const idealEffort = d.effort;
    const measured = s.measuredEffort ?? (s.measuredEffortKgf !== undefined ? s.measuredEffortKgf * g : undefined);
    /* A stated effort replaces the bench's own, but never undercuts it: less
     * effort than a frictionless machine needs is not friction, it is a wrong
     * setup (or the wrong g). */
    if (measured !== undefined && idealEffort && measured < idealEffort * (1 - 1e-6)) {
        problems.push(`the measured effort ${+measured.toPrecision(4)} N is less than the ${+idealEffort.toPrecision(4)} N this machine needs with no friction at all — check the arrangement, the load and g`);
    }
    const effort = measured ?? idealEffort;
    const vr = d.velocityRatio;
    const ma = effort ? load / effort : null;
    const rise = s.loadRise;
    const readings: Record<string, Reading> = {
        load,
        effort,
        idealEffort,
        mechanicalAdvantage: ma,
        velocityRatio: vr,
        efficiency: ma !== null && vr ? (100 * ma) / vr : null,
        ceilingLoad: ceilingLoad(x),
        tension: s.arrangement === 'movable-combination' ? tension(x, `s${s.movable}`) : tension(x, 'r1'),
        effortDistance: rise !== undefined && vr ? vr * rise : null,
        workOutput: rise !== undefined ? load * rise : null,
        workInput: rise !== undefined && vr && effort ? effort * vr * rise : null,
    };
    const indexed = s.arrangement === 'movable-combination'
        ? { tension: Object.fromEntries(Array.from({ length: s.movable }, (_, k) => [String(k + 1), tension(x, `s${k + 1}`)])) }
        : undefined;
    return { readings, ...(indexed ? { indexed } : {}), problems };
}

function solveBodies(s: Exclude<MechanicsSetup, { load: Load }>): BenchSolution {
    const w = M.released(buildMechanics(s));
    const x = solveWorld(w);
    const problems = stopping(w, x.sol);
    const a = accelerationOf(x, 'm1');
    const readings: Record<string, Reading> = {
        acceleration: a,
        moves: a > 1e-6 ? 'yes' : 'no',
        tension: s.arrangement === 'incline' ? null : tension(x, 'r1'),
        normal: x.sol.force['rest:m1'] ?? null,
        friction: s.arrangement === 'atwood' ? null : Math.abs(x.sol.force['grip:m1'] ?? 0),
    };
    return { readings, problems };
}

export function solveMechanics(s: MechanicsSetup): BenchSolution {
    return isMachine(s) ? solveMachine(s) : solveBodies(s);
}

// ── The manifest ────────────────────────────────────────────────────────────

const READOUTS: ReadoutDecl[] = [
    { key: 'load', label: 'Load (the weight lifted, excluding the pulleys)', unit: 'N' },
    { key: 'effort', label: 'Effort — the measured one when the question states it, else what the frictionless machine needs', unit: 'N' },
    { key: 'idealEffort', label: 'Effort the machine would need with no friction', unit: 'N' },
    { key: 'mechanicalAdvantage', label: 'Mechanical advantage (load ÷ effort)', unit: '' },
    { key: 'velocityRatio', label: 'Velocity ratio (effort distance ÷ load distance)', unit: '' },
    { key: 'efficiency', label: 'Efficiency (MA ÷ VR)', unit: '%' },
    { key: 'ceilingLoad', label: 'Total force on the ceiling supports', unit: 'N' },
    { key: 'tension', label: 'Tension in the string', unit: 'N', on: 'for movable-combination, the string number counted from the load: 1 is under the lowest pulley' },
    { key: 'effortDistance', label: 'Distance the effort moves (needs loadRise)', unit: 'm' },
    { key: 'workOutput', label: 'Work done on the load (needs loadRise)', unit: 'J' },
    { key: 'workInput', label: 'Work done by the effort (needs loadRise)', unit: 'J' },
    { key: 'acceleration', label: 'Acceleration of the system', unit: 'm/s²' },
    { key: 'moves', label: 'Does it move when released?', categories: ['yes', 'no'] },
    { key: 'normal', label: 'Normal force on the block', unit: 'N' },
    { key: 'friction', label: 'Friction force on the block', unit: 'N' },
];

const VOCABULARY = `Mechanics bench — setup vocabulary v1. Units: kg, N, m, degrees.

setup is ONE of these arrangements:

Pulley machines (the load hangs still; the effort is what holds it). Every one also takes the optional
"pulleyMass", "measuredEffort", "loadRise" and "g" described below:
  { "arrangement": "single-fixed",        "load": LOAD }
  { "arrangement": "single-movable",      "load": LOAD }   // effort pulls up
  { "arrangement": "movable-tied-off",    "load": LOAD }   // one end tied to the ceiling, over a fixed pulley to the effort
  { "arrangement": "block-and-tackle",    "segments": 2..6, "load": LOAD }
        // segments = rope segments holding the lower block = VR. "A block and tackle of 5 pulleys" has 5 segments.
  { "arrangement": "movable-combination", "movable": 1..4, "load": LOAD }
        // n movable pulleys each on its own string (MA = VR = 2ⁿ).
  LOAD is { "mass": kg } or { "weight": N }. A load in kgf is its mass in kg: 48 kgf → { "mass": 48 }.
  "pulleyMass": kg — ONLY when the question states a pulley's or lower block's weight. For a block and tackle it is
        the whole lower block; for a movable combination, each movable pulley. (A weight in kgf is its mass in kg.)
  "measuredEffort": N — when the question states the effort actually needed and it is MORE than the machine needs
        ideally ("an effort of 1000 N raises 4500 N"). The difference is friction: the bench's pulleys stay
        frictionless and give the VR; MA and efficiency come from the stated load and this effort. An effort stated
        in kgf goes in "measuredEffortKgf" instead, unconverted. NEVER invent a pulleyMass to explain a stated effort — a pulley weight the question
        does not mention is a false scene.
  "loadRise": m — how far the load is raised, for distances and work.

Connected bodies (released from rest; nothing holds them):
  { "arrangement": "atwood",       "mass1": kg, "mass2": kg, "g": G }                 // two masses over one fixed pulley
  { "arrangement": "table-pulley", "onTable": kg, "hanging": kg, "mu": 0.., "g": G }  // block on a table, string over the edge pulley to a hanging mass
  { "arrangement": "incline",      "mass": kg, "angle": 5..60, "mu": 0.., "g": G }    // one block let go on a slope

"g": 9.8, 9.81 or 10 — ONLY when the question states it. When it does not, leave "g" out: the bench uses 9.8, and
if the key disagrees the check itself tries 10. Never pick g to suit the key. (Every arrangement, including the
connected bodies above, takes g this way.)

The bench's strings and pulleys are ideal. So:
  - a loss the question states as an effort → "measuredEffort" (above).
  - a loss stated as an efficiency ("efficiency 80%, find the effort") → an inverse on measuredEffort:
    "inverse": { "param": "measuredEffort", "readout": "efficiency", "equals": 80, "range": [ideal effort, 20 × it] },
    with measuredEffort set to any value in that range, and an ask for "effort".
  - levers, wheel and axle, gears, inclined planes as machines → cannot, outside-bench.
  - blocks pushed in contact, a string pulled sideways by a force, more than two connected bodies → cannot, outside-bench.

Inverse: when a given is what is asked for ("what load can an effort of 200 N lift?"), set the parameter to any
value in range and add "inverse": { "param": "load.mass", "readout": "effort", "equals": 200, "range": [0.1, 1000] },
and ask for the readout that answers it ("load", in N — a key in kgf is converted).

Example — "A block and tackle has 4 pulleys. The lower block weighs 2 kgf. Find the effort to lift 48 kgf and the efficiency. (g = 10)":
  { "arrangement": "block-and-tackle", "segments": 4, "load": { "mass": 48 }, "pulleyMass": 2, "g": 10 }
  asks: [{ "part": "a", "readout": "effort" }, { "part": "b", "readout": "efficiency" }]

Example — "A block and tackle of 5 pulleys needs an effort of 1000 N to raise 4500 N. Find its MA, VR and efficiency.":
  { "arrangement": "block-and-tackle", "segments": 5, "load": { "weight": 4500 }, "measuredEffort": 1000 }
  asks: [{ "readout": "mechanicalAdvantage" }, { "readout": "velocityRatio" }, { "readout": "efficiency" }]`;

export const mechanicsBench: BenchManifest<MechanicsSetup, M.World> = {
    bench: 'mechanics',
    title: 'Mechanics bench',
    specVersion: 1,
    covers: [
        // Physics → Machines, and its topics
        'f5422410-0d42-46ec-97ab-84ffb1f0a5fa',
        '69ba53e7-7e9c-425d-a427-3a5a3f1f981b', // Pulleys
        'f5032919-becc-4a55-876b-b5ac8dec1992', // Simple Machines & Efficiency
        // Newton's Laws → Pulleys & Strings (connected bodies)
        '7b66fccd-1ecf-4955-80d9-4d57cb79bb3a',
    ],
    exact: true,
    vocabulary: VOCABULARY,
    readouts: READOUTS,
    assumptions: ['strings are massless and inextensible', 'pulleys are frictionless'],
    unstated: [{ path: 'g', label: 'g', default: DEFAULT_G, alternatives: [10] }],
    validate: validateMechanics,
    build: buildMechanics,
    solve: solveMechanics,
};
